import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import { CheckIcon, SelectorIcon } from "@heroicons/react/solid";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../Components/Button";

import {
  classNames,
  convertToTitleCase,
  formatCurrency,
  getErrorMessages,
} from "../../Utils";
import { ErrorList } from "../../Components/ErrorList";
import { EmptyList } from "../../Components/EmptyList";
import { useLocation, useNavigate } from "react-router-dom";
import {
  requestAddMainBusiness,
  requestListBusiness,
  useAuthDispatch,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../Context";
import {
  useAccountingDispatch,
  useAccountingState,
} from "../../Context/accounting/context";
import {
  clearAccountBalanceData,
  clearAccountingAccount,
  clearAcctAccountError,
  clearInitAccountStatus,
  requestDeleteAccount,
  requestGetAccountBalance,
  requestInitAccount,
  requestListAccount,
  requestUpdateAccount,
} from "../../Context/accounting/actions";
import { Pagination } from "../../Components/Pagination";
import TableSkeleton from "../../Components/TableSkeleton";
import { PencilAltIcon, TrashIcon } from "@heroicons/react/outline";
import { ModalAlert } from "../../Components/ModalAlert";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { Modal } from "../../Components/Modal";
import EmptyState from "../../Components/EmptyState";
import { EmptyBusinessSkeleton } from "../../Components/EmptyBusinessSkeleton";
import FormLabel from "../../Components/FormLabel";
import { Alert } from "../../Components/Alert";
import EmptyBusiness from "../../Components/EmptyBusiness";
import { isBusinessMainBlocked } from "../../Constants/business";

export const Account = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthState();

  const initialLoad = useRef(false);

  const [selectedBusiness, setselectedBusiness] = useState({});
  const [page, setpage] = useState(1);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [accountCode, setAccountCode] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountId, setAccountId] = useState("");
  const [fieldErrorModal, setfieldErrorModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const [nameError, setnameError] = useState(null);

  const businessTransactionDispatch = useBusinessTransactionDispatch();
  const authDispatch = useAuthDispatch();

  const { businesses, loading } = useBusinessTransactionState();
  const dispatch = useAccountingDispatch();
  const {
    accounts,
    accountError,
    accountNextTokens,
    accountLoading,
    submitLoading,
    submitError,
    accountID,
    deletedID,
    deleteLoading,
    deleteError,
    initAccountLoading,
    initAccountStatus,
    accountBalances,
    accountBalancesLoading,
  } = useAccountingState();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const businessIdParam = searchParams.get("id");

  useEffect(() => {
    if (user.id) {
      requestListBusiness(businessTransactionDispatch, {
        owner: user.id,
        page: 1,
        limit: 10,
      });
    }
  }, [businessTransactionDispatch, user.id]);

  useEffect(() => {
    // Function to set selected business
    const setSelectedBusinessLogic = async () => {
      if (businessIdParam) {
        if (businesses.length > 0) {
          const business = businesses.find(
            (item) => item.value === businessIdParam,
          );
          if (business) {
            setselectedBusiness({
              ...business,
              label: business.name,
              value: business.id,
            });
          } else {
            // Handle if business is not found, select index 0
            setselectedBusiness({
              ...businesses[0],
              label: businesses[0].name,
              value: businesses[0].id,
            });
          }
        } else {
          // Handle if businesses are empty, no business to select
        }
      } else if (user.mainBusiness) {
        // Set selectedBusiness directly from user.mainBusiness
        setselectedBusiness({
          ...user.mainBusiness,
          label: user.mainBusiness.name,
          value: user.mainBusiness.id,
        });
      } else if (businesses.length > 0) {
        // Set selected business to the first item in the list
        // Prefer a healthy business: a RESTRICTED / PENDING_DELETION one must
        // not become the account-wide main business. Falls back to the first
        // entry when every business is blocked.
        const mainCandidate =
          businesses.find((business) => !isBusinessMainBlocked(business)) ||
          businesses[0];
        setselectedBusiness({
          ...mainCandidate,
          label: mainCandidate.name,
          value: mainCandidate.id,
        });
        requestAddMainBusiness(authDispatch, {
          businessID: mainCandidate.id,
        });
      } else {
        // Handle if businesses are empty, no business to select
      }
    };

    setSelectedBusinessLogic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businesses, businessIdParam, user.mainBusiness]);

  useEffect(() => {
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedBusiness.value]);

  useEffect(() => {
    if (accountID) {
      clearAccountingAccount(dispatch);
    }
    clearAcctAccountError(dispatch);
    return () => {
      clearAccountBalanceData(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedBusiness.value && accounts.length > 0) {
      requestGetAccountBalance(dispatch, {
        businessID: selectedBusiness.value,
        accountIDs: accounts.map((a) => a.id),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts]);

  useEffect(() => {
    if (accountID) {
      setShowConfirmModal(false);
      setShowEditModal(false);
      setShowSuccessModal(true);
    } else if (submitError || deleteError) {
      setShowConfirmModal(false);
      setShowEditModal(false);
      setShowErrorModal(true);
    }
  }, [accountID, submitError, deleteError]);

  useEffect(() => {
    if (!deletedID) {
      return;
    }
    setShowConfirmModal(false);
    setShowSuccessModal(true);
  }, [deletedID]);

  useEffect(() => {
    if (initAccountStatus === "SUCCESS") {
      requestListAccount(dispatch, {
        businessID: selectedBusiness.id,
      });
      navigate(`/account?id=${selectedBusiness?.id}`);
      clearInitAccountStatus(dispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initAccountStatus]);

  useEffect(() => {
    const isNameValid = accountName.length > 0;
    if (isNameValid) {
      setErrorMessage("name", "");
    } else {
      setErrorMessage("name", "Account name is required.");
    }
  }, [accountName]);

  const setErrorMessage = (field, message) => {
    switch (field) {
      case "name":
        setnameError(message);
        break;
      default:
        break;
    }
  };

  const validateInput = () => {
    let currentValidName = true;
    const isNameValid = accountName.length > 0;

    currentValidName = isNameValid;

    return currentValidName;
  };

  const onLoad = () => {
    if (selectedBusiness.value) {
      requestListAccount(dispatch, {
        page: 1,
        businessID: selectedBusiness.value,
      });
    }
  };

  const onCreate = () => {
    clearAcctAccountError(dispatch);
    navigate(`/account/create?id=${selectedBusiness.value}`);
  };

  const onSetOpeningBalance = () => {
    clearAcctAccountError(dispatch);
    navigate(`/opening-balance?id=${selectedBusiness.value}`);
  };

  const onManagePeriod = () => {
    clearAcctAccountError(dispatch);
    navigate(`/period-close?id=${selectedBusiness.value}`);
  };

  const onUpdate = () => {
    const validForm = validateInput();

    if (validForm) {
      if (accountId && accountCode && accountName) {
        setfieldErrorModal(false);

        const input = {
          account_id: accountId,
          name: accountName,
        };

        requestUpdateAccount(dispatch, { input });
      }
    } else {
      setfieldErrorModal(true);
      return;
    }
  };

  const onDelete = () => {
    if (!accountId) {
      return;
    }

    requestDeleteAccount(dispatch, {
      id: accountId,
    });
  };

  const onNext = () => {
    initialLoad.current = true;
    const nextPage = page + 1;
    setpage(nextPage);
    if (accountNextTokens[nextPage]) {
      requestListAccount(dispatch, {
        page: nextPage,
        limit: 10,
        businessID: selectedBusiness.value,
        nextTokens: accountNextTokens[nextPage],
      });
    }
  };

  const onPrev = () => {
    initialLoad.current = true;
    const prevPage = page - 1;
    setpage(prevPage);
    if (prevPage > 0) {
      requestListAccount(dispatch, {
        page: prevPage,
        businessID: selectedBusiness.value,
        nextTokens: accountNextTokens[prevPage],
      });
    }
  };

  const onPageChange = (selectedPage) => {
    setpage(selectedPage);
    if (accountNextTokens[selectedPage] || selectedPage === 1) {
      requestListAccount(dispatch, {
        page: selectedPage,
        businessID: selectedBusiness.value,
        nextTokens: accountNextTokens[selectedPage],
      });
    }
  };

  const handleInitAccount = () => {
    requestInitAccount(dispatch, {
      businessID: selectedBusiness?.id,
    });
  };

  const EmptyAccount = () => {
    return initAccountLoading ? (
      <EmptyBusinessSkeleton />
    ) : accountError ? (
      <div className="bg-white shadow-sm max-w-7xl mx-auto overflow-hidden px-4 py-6 sm:px-6 rounded-t-lg">
        <Alert
          title="Error"
          type="error"
          message={accountError?.message || "Error fetch accounting account."}
          errors={accountError?.errors}
        />
      </div>
    ) : (
      <EmptyState
        titleKey="No Accounts"
        descriptionKey="Please create an account first"
      >
        <div className="flex flex-col sm:flex-row justify-between gap-4 sm:gap-6">
          <Button
            outline
            className="flex-1"
            onClick={handleInitAccount}
            title="Create Automatically"
          />

          <Button
            outline
            className="flex-1"
            onClick={() =>
              navigate(`/account/import?id=${selectedBusiness.id}`)
            }
            title="Import Using XLSX File"
          />
        </div>
      </EmptyState>
    );
  };

  if (businesses.length === 0) {
    return <EmptyBusiness loading={loading} />;
  }

  if (accounts.length === 0 && !initialLoad.current) {
    return accountLoading ? <EmptyBusinessSkeleton /> : <EmptyAccount />;
  }

  return (
    <div className="space-y-4">
      {(submitError || deleteError) && (
        <ModalConfirm
          open={showErrorModal}
          type="ALERT"
          setOpen={(open) => {
            setShowErrorModal(open);
          }}
          title="Error"
          description={getErrorMessages(submitError || deleteError)}
        />
      )}
      {fieldErrorModal && (
        <ModalConfirm
          open={fieldErrorModal}
          type="WARNING"
          setOpen={(open) => {
            setfieldErrorModal(open);
          }}
          title="Incomplete Form"
          description={t("Please fill out all fields.")}
        />
      )}
      <ModalAlert
        open={showConfirmModal}
        setOpen={(open) => {
          setShowConfirmModal(open);
        }}
        title="Delete Account"
        description="Accounts can only be deleted if they have not been used in any transaction or setting. Continue?"
        confirmButtonText="Yes"
        cancelButtonText="Cancel"
        loadingButton={deleteLoading}
        disabledButton={deleteLoading}
        onConfirm={onDelete}
      />
      <ModalConfirm
        open={showSuccessModal}
        type="SUCCESS"
        setOpen={(open) => {
          setShowSuccessModal(open);
        }}
        title="Success"
        description={
          deletedID
            ? "The accounting account has been successfully deleted"
            : "The accounting account has been successfully changed"
        }
        onConfirm={() => {
          clearAccountingAccount(dispatch);
        }}
      />
      <Modal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        title={t("Change Accounting Account")}
        footer={
          <div className="p-4 flex justify-end items-center bg-gray-50 rounded-b-lg">
            <Button
              type="submit"
              disabled={submitLoading}
              loading={submitLoading}
              title="Save"
              onClick={onUpdate}
            />
          </div>
        }
      >
        <div className="flex flex-col gap-4 p-4 text-sm text-gray-700">
          <div>
            <FormLabel htmlFor="code" label={t("Code")} required />
            <input
              type="text"
              name="code"
              value={accountCode}
              disabled
              className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100"
            />
          </div>
          <div>
            <FormLabel htmlFor="name" label={t("Name")} required />
            <input
              type="text"
              name="name"
              value={accountName}
              placeholder={t("Enter Account Name")}
              onChange={(e) => setAccountName(e.target.value)}
              className={classNames(
                nameError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm",
              )}
            />
            {nameError && (
              <div className="mt-1 text-red-600 text-sm">{t(nameError)}</div>
            )}
          </div>
        </div>
      </Modal>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 mb-4 sm:w-full">
        <div>
          <Listbox
            value={selectedBusiness}
            onChange={(value) => {
              initialLoad.current = false;
              setselectedBusiness(value);
            }}
          >
            {({ open }) => (
              <>
                <ListboxButton className="flex items-center justify-between w-full sm:w-48 py-2 px-3 border border-gray-300 rounded-md bg-white shadow-sm text-sm">
                  <span className="max-w-full ml-3 mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                    {selectedBusiness?.label || t("Business Name")}
                  </span>
                  <SelectorIcon className="w-5 h-5 text-gray-500" />
                </ListboxButton>
                <Transition
                  show={open}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-11/12 sm:w-auto rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                    {businesses.map((business) => (
                      <ListboxOption
                        key={business.value}
                        className={({ focus }) =>
                          classNames(
                            focus ? "text-white bg-blue-600" : "text-gray-900",
                            "cursor-default select-none relative py-2 pl-3 pr-9",
                          )
                        }
                        value={business}
                      >
                        {({ selected, focus }) => (
                          <>
                            <div className="flex items-center justify-between">
                              <span
                                className={classNames(
                                  selected ? "font-semibold" : "font-normal",
                                  "ml-3 mr-2 block truncate",
                                )}
                              >
                                {business.label}
                              </span>
                              {selected ? (
                                <span
                                  className={classNames(
                                    focus ? "text-white" : "text-blue-600",
                                    "absolute inset-y-0 right-0 flex items-center pr-4",
                                  )}
                                >
                                  <CheckIcon className="h-5 w-5" />
                                </span>
                              ) : null}
                            </div>
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Transition>
              </>
            )}
          </Listbox>
        </div>
        <div className="flex flex-col flex-wrap sm:flex-row gap-4 justify-start sm:justify-end w-full">
          <div>
            {/* v1's only entry point to Tutup Buku. */}
            <Button
              className="w-full"
              type="button"
              onClick={onManagePeriod}
              outline
              title={t("accounting.period.label")}
            />
          </div>
          <div>
            <Button
              className="w-full"
              type="button"
              onClick={onSetOpeningBalance}
              outline
              title={t("Set Opening Balance")}
            />
          </div>
          <div>
            <Button
              className="w-full"
              type="button"
              onClick={onCreate}
              title={t("Create New Account")}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col">
        <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Code")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Name")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Category")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Account Type")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-right text-gray-900 whitespace-nowrap">
                      {t("Balance")}
                    </th>
                    <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                      {t("Action")}
                    </th>
                  </tr>
                </thead>
                {accountLoading ? (
                  <TableSkeleton rows={10} columns={6} />
                ) : (
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {accounts.length > 0 ? (
                      accounts
                        .filter(
                          (account) =>
                            accountBalancesLoading ||
                            Object.keys(accountBalances).length === 0 ||
                            accountBalances[account.id],
                        )
                        .map((account) => {
                          return (
                            <tr key={account.id}>
                              <td className="px-4 py-2 font-medium text-sm text-gray-00 whitespace-nowrap ">
                                {account.code}
                              </td>
                              <td className="px-4 py-2 font-medium text-sm text-gray-00 whitespace-nowrap ">
                                {account.name}
                              </td>
                              <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                                {t(convertToTitleCase(account.category))}
                              </td>
                              <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                                {t(convertToTitleCase(account.type))}
                              </td>
                              <td className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                                {accountBalancesLoading ? (
                                  <div className="h-4 w-24 bg-gray-100 rounded animate-pulse ml-auto" />
                                ) : accountBalances?.[account.id] ? (
                                  <span
                                    className={
                                      accountBalances[account.id].balance >= 0
                                        ? "text-green-600 font-medium"
                                        : "text-red-600 font-medium"
                                    }
                                  >
                                    {formatCurrency(
                                      accountBalances[account.id].balance,
                                    )}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-4 py-2 text-sm text-left text-gray-700 whitespace-nowrap">
                                {account.editable && (
                                  <div className="flex flex-auto gap-2">
                                    <button
                                      onClick={() => {
                                        setShowEditModal(true);
                                        setAccountCode(account.code);
                                        setAccountName(account.name);
                                        setAccountId(account.id);
                                      }}
                                    >
                                      <PencilAltIcon className="h-5 w-5 text-blue-500" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        setShowConfirmModal(true);
                                        setAccountId(account.id);
                                      }}
                                    >
                                      <TrashIcon className="h-5 w-5 text-red-500" />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })
                    ) : (
                      <tr>
                        <td className="p-4" colSpan={6}>
                          {accountError ? (
                            <ErrorList
                              message={
                                accountError?.message ||
                                "Error fetching accounts."
                              }
                              errors={accountError?.errors}
                            />
                          ) : (
                            <EmptyList title="No Accounts" />
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                )}
              </table>
              <Pagination
                page={page}
                onPrev={onPrev}
                onNext={onNext}
                onPageChange={onPageChange}
                nextTokens={accountNextTokens}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
